import type {
  Meeting,
  MeetingDetail,
  MeetingQuestion,
  MeetingVisualization,
  UploadResult,
  VisualizationKind,
} from '@shared/contract';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body instanceof FormData) init.body = body;
  else if (body !== undefined) {
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event('mr:unauthorized'));
    throw new ApiError(res.status, data.error ?? 'error', data.message ?? `Fel ${res.status}`);
  }
  return data as T;
}

export interface User {
  id: string;
  email: string;
}

export const api = {
  me: () => request<User>('GET', '/api/auth/me'),
  authConfig: () => request<{ allow_registration: boolean }>('GET', '/api/auth/config'),
  login: (email: string, password: string) => request<User>('POST', '/api/auth/login', { email, password }),
  register: (email: string, password: string) => request<User>('POST', '/api/auth/register', { email, password }),
  logout: () => request<void>('POST', '/api/auth/logout'),

  listMeetings: () => request<Meeting[]>('GET', '/api/meetings'),
  createMeeting: (title?: string) => request<Meeting>('POST', '/api/meetings', { title }),
  getMeeting: (id: string) => request<MeetingDetail>('GET', `/api/meetings/${id}`),
  renameMeeting: (id: string, title: string) => request<Meeting>('PATCH', `/api/meetings/${id}`, { title }),
  start: (id: string) => request<Meeting>('POST', `/api/meetings/${id}/start`),
  pause: (id: string) => request<Meeting>('POST', `/api/meetings/${id}/pause`),
  resume: (id: string) => request<Meeting>('POST', `/api/meetings/${id}/resume`),
  finish: (id: string, lastSequence: Record<string, number>) =>
    request<Meeting>('POST', `/api/meetings/${id}/finish`, { last_sequence: lastSequence }),
  deleteMeeting: (id: string) => request<void>('DELETE', `/api/meetings/${id}`),
  requestSummary: (id: string) => request<{ job_id: number }>('POST', `/api/meetings/${id}/summary`),
  visualize: (id: string, kind: VisualizationKind) =>
    request<MeetingVisualization>('POST', `/api/meetings/${id}/visualizations`, { kind }),
  ask: (id: string, question: string) => request<MeetingQuestion>('POST', `/api/meetings/${id}/questions`, { question }),
  uploadSegment: (id: string, form: FormData) => request<UploadResult>('POST', `/api/meetings/${id}/audio`, form),
  exportUrl: (id: string, format: 'md' | 'txt') => `/api/meetings/${id}/export?format=${format}`,
  exportText: async (id: string, format: 'md' | 'txt') => {
    const res = await fetch(`/api/meetings/${id}/export?format=${format}`, { credentials: 'same-origin' });
    if (!res.ok) throw new ApiError(res.status, 'export_failed', 'Exporten misslyckades.');
    return res.text();
  },
};
